/**
 * SuppliersPage — COM-01: Gestión de Proveedores
 */
import { ordenControlado } from '@/components/ds/ordenControlado'
import { sortStateToParam, type SortState } from '@/components/SortableHeader'
import { useState, useMemo } from 'react'
import { Plus, Upload, PencilLine, UserX, UserCheck, Eye, Loader2, Truck } from 'lucide-react'
import { FilterBar, useFilters, type FilterConfig } from '@/components/filters'
import { EmptyState } from '@/components/EmptyState'
import { TableSkeleton } from '@/components/Skeleton'
import { DataTable, type DataTableColumn } from '@/components/ds/DataTable'
import { KebabMenu } from '@/components/KebabMenu'
import { ConfirmModal } from '@/components/ConfirmModal'
import {
  useSuppliers, useCreateSupplier, useUpdateSupplier,
  useDeactivateSupplier, useRestoreSupplier, useImportSuppliersCSV,
  type Supplier, type SupplierListItem,
  useSupplier,
} from '../hooks/usePurchases'
import { SupplierModal } from './SupplierModal'
import { formatRut } from '@/lib/utils'
import { PageHeader } from '@/components/HelpPanel'
import { HELP } from '@/data/helpContent'
import { ImportCSVMapperModal, type SystemField } from '@/components/ImportCSVMapperModal'
import { usePermission } from '@/hooks/usePermission'
import { Pagination } from '@/components/ds/Pagination'
import { useTenantLocale } from '@/lib/locale'

const SUPPLIER_FIELDS: SystemField[] = [
  { key: 'legal_name', label: 'Razón Social', required: true, aliases: ['razon_social', 'legal_name', 'nombre'] },
  { key: 'trade_name', label: 'Nombre Fantasía', aliases: ['nombre_fantasia', 'trade_name'] },
  { key: 'tax_id', label: 'RUT / ID Tributario', aliases: ['rut', 'tax_id', 'ruc', 'cuit'] },
  { key: 'person_type', label: 'Tipo Persona', aliases: ['tipo_persona', 'person_type'] },
  { key: 'contact_name', label: 'Nombre Contacto', aliases: ['contacto', 'contact_name', 'nombre_contacto'] },
  { key: 'email', label: 'Email', aliases: ['correo', 'email'] },
  { key: 'phone', label: 'Teléfono', aliases: ['telefono', 'phone'] },
  { key: 'address_street', label: 'Dirección', aliases: ['direccion', 'address_street'] },
  { key: 'address_commune', label: 'Comuna', aliases: ['comuna', 'address_commune'] },
  { key: 'address_city', label: 'Ciudad', aliases: ['ciudad', 'address_city'] },
  { key: 'address_region', label: 'Región', aliases: ['region', 'address_region'] },
  { key: 'giro', label: 'Giro Comercial', aliases: ['giro', 'giro_comercial'] },
  { key: 'website', label: 'Sitio Web', aliases: ['sitio_web', 'web', 'website'] },
  { key: 'notes', label: 'Notas', aliases: ['notas', 'notes'] },
]

function StatusBadge({ active }: { active: boolean }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium
      ${active ? 'bg-success-surface text-success-content' : 'bg-surface-muted text-content-muted'}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${active ? 'bg-success-solid' : 'bg-content-subtle'}`} />
      {active ? 'Activo' : 'Inactivo'}
    </span>
  )
}

/**
 * Las columnas del listado de proveedores.
 *
 * 🔴 Esto era `SupplierRow`, un componente que emitía su propio `<tr>` — el
 * bloqueo «componente-fila» del inventario. Era render puro, así que se desarma
 * en columnas sin perder nada.
 */
function columnasDeProveedor({
  canEdit, onEdit, onView, onDeactivate, onRestore,
}: {
  canEdit: boolean
  onEdit: (s: SupplierListItem) => void
  onView: (s: SupplierListItem) => void
  onDeactivate: (s: SupplierListItem) => void
  onRestore: (s: SupplierListItem) => void
}): DataTableColumn<SupplierListItem>[] {
  return [
    { key: 'legal_name', header: 'Proveedor', sortable: true,
      render: s => (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-warning-surface flex items-center justify-center shrink-0">
            <Truck size={14} className="text-warning-solid" />
          </div>
          <div>
            <p className="text-sm font-medium text-content">{s.legal_name}</p>
            {s.trade_name && <p className="text-xs text-content-subtle">{s.trade_name}</p>}
          </div>
        </div>
      ) },
    { key: 'tax_id', header: 'RUT', sortable: true,
      render: s => <span className="text-content-muted">{formatRut(s.tax_id)}</span> },
    { key: 'email', header: 'Email', sortable: true,
      render: s => <span className="text-content-muted">{s.email || '—'}</span> },
    { key: 'phone', header: 'Teléfono', sortable: true,
      render: s => <span className="text-content-muted">{s.phone || '—'}</span> },
    { key: 'is_active', header: 'Estado', sortable: true,
      sortValue: s => (s.is_active ? 1 : 0),
      render: s => <StatusBadge active={s.is_active} /> },
    { key: 'acciones', header: '', width: 180,
      render: s => (
        <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
          {canEdit ? (
            <>
              <button
                onClick={() => onEdit(s)}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs text-content-muted border border-divider rounded-lg hover:bg-soft transition-colors"
                title="Editar"
              >
                <PencilLine size={11} />Editar
              </button>
              <KebabMenu items={[
                { label: s.is_active ? 'Desactivar' : 'Reactivar', icon: s.is_active ? <UserX size={14} /> : <UserCheck size={14} />, onClick: () => (s.is_active ? onDeactivate(s) : onRestore(s)), className: s.is_active ? 'text-danger-content-soft hover:bg-danger-surface' : 'text-success-content-soft hover:bg-success-surface' },
              ]} />
            </>
          ) : (
            <button
              onClick={() => onView(s)}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs text-content-muted border border-divider rounded-lg hover:bg-soft transition-colors"
              title="Ver detalles"
            >
              <Eye size={11} />Ver
            </button>
          )}
        </div>
      ) },
  ]
}

export function SuppliersPage() {
  const locale = useTenantLocale()
  const [page, setPage] = useState(1)
  // El orden va al SERVIDOR (`?ordering=`): esta lista la pagina el backend,
  // y ordenar en el navegador ordenaría solo la página visible.
  const [orden, setOrden] = useState<SortState | null>(null)
  const { can } = usePermission()
  const canCreate = can('purchases.suppliers', 'create')
  const canEdit = can('purchases.suppliers', 'edit')

  const filterConfig: FilterConfig = useMemo(() => [
    { key: 'search', type: 'search' as const, placement: 'quick' as const,
      placeholder: 'Nombre, RUT, email...' },
    { key: 'active', type: 'segment' as const, placement: 'quick' as const,
      options: [
        { value: 'true', label: 'Activos' },
        { value: 'all', label: 'Todos' },
      ] },
  ], [])

  const filters = useFilters(filterConfig, { onFilterChange: () => setPage(1) })
  const [modalOpen, setModalOpen] = useState(false)
  const [modalReadOnly, setModalReadOnly] = useState(false)
  const [editingSupplier, setEditingSupplier] = useState<SupplierListItem | null>(null)
  // El modal se llena con el DETALLE, no con la fila del listado (M03-VC-05).
  const { data: detalleProveedor } = useSupplier(editingSupplier?.id ?? null)
  const [confirmDeactivate, setConfirmDeactivate] = useState<SupplierListItem | null>(null)
  const [apiError, setApiError] = useState<string | null>(null)
  const [importOpen, setImportOpen] = useState(false)

  const { data, isLoading } = useSuppliers({ ordering: sortStateToParam(orden),
    search: filters.params.search,
    active: (filters.params.active || 'true') as 'true' | 'all',
    page,
    page_size: 20,
  })

  const createSupplier = useCreateSupplier()
  const updateSupplier = useUpdateSupplier(editingSupplier?.id ?? '')
  const deactivate = useDeactivateSupplier()
  const restore = useRestoreSupplier()
  const importMutation = useImportSuppliersCSV()

  const handleSave = (formData: Partial<Supplier>) => {
    setApiError(null)
    if (editingSupplier) {
      updateSupplier.mutate(formData, {
        onSuccess: () => { setModalOpen(false); setEditingSupplier(null) },
        onError: (e: unknown) => {
          const err = e as { response?: { data?: { error?: { message?: string } } } }
          setApiError(err.response?.data?.error?.message ?? 'Error al guardar')
        },
      })
    } else {
      createSupplier.mutate(formData, {
        onSuccess: () => { setModalOpen(false) },
        onError: (e: unknown) => {
          const err = e as { response?: { data?: { error?: { message?: string } } } }
          setApiError(err.response?.data?.error?.message ?? 'Error al crear')
        },
      })
    }
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <PageHeader
        title="Proveedores"
        subtitle={`${data?.count ?? 0} proveedor${data?.count !== 1 ? 'es' : ''}`}
        help={HELP['purchases.suppliers']}
      >
        {canCreate && (
          <button
            onClick={() => setImportOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-content border border-divider rounded-lg hover:bg-soft transition-colors">
            <Upload size={15} />Importar CSV
          </button>
        )}
        {canCreate && (
          <button
            onClick={() => { setEditingSupplier(null); setApiError(null); setModalReadOnly(false); setModalOpen(true) }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-on-primary text-sm rounded-lg hover:bg-primary-hover transition-colors">
            <Plus size={15} />Nuevo Proveedor
          </button>
        )}
      </PageHeader>

      {/* Filtros */}
      <FilterBar config={filterConfig} filters={filters} />

      {/* Tabla */}
      <div className="bg-glass backdrop-blur-glass rounded-card border border-divider overflow-x-auto shadow-card">
        {isLoading ? (
          <TableSkeleton rows={8} cols={6} />
        ) : !data?.suppliers.length ? (
          <EmptyState icon={Truck} size="md" title="No hay proveedores registrados" />
        ) : (
          <DataTable<SupplierListItem>
            {...ordenControlado(orden, s => { setOrden(s); setPage(1) })}
            columns={columnasDeProveedor({
              canEdit,
              onEdit: sup => { setEditingSupplier(sup); setModalReadOnly(false); setApiError(null); setModalOpen(true) },
              onView: sup => { setEditingSupplier(sup); setModalReadOnly(true); setApiError(null); setModalOpen(true) },
              onDeactivate: sup => setConfirmDeactivate(sup),
              onRestore: sup => restore.mutate(sup.id),
            })}
            rows={data.suppliers}
            rowKey={s => s.id}
            minWidth={900}
            surface={false}
            onRowClick={s => {
              setEditingSupplier(s); setModalReadOnly(!canEdit); setApiError(null); setModalOpen(true)
            }}
            caption="Proveedores registrados"
          />
        )}
      </div>

      {/* Paginación */}
      {data && data.pages > 1 && (
        <Pagination
          page={page}
          pageSize={20}
          total={data?.count ?? 0}
          onPageChange={setPage}
          locale={locale}
        />
      )}

      {/* Modal crear/editar/ver */}
      {modalOpen && (
        <SupplierModal
          supplier={editingSupplier ? (detalleProveedor ?? null) : null}
          loading={!!editingSupplier && !detalleProveedor}
          onSave={handleSave}
          onClose={() => { setModalOpen(false); setEditingSupplier(null) }}
          isPending={createSupplier.isPending || updateSupplier.isPending}
          error={apiError}
          readOnly={modalReadOnly}
        />
      )}

      {/* Confirm desactivar */}
      <ConfirmModal
        open={confirmDeactivate !== null}
        message={`¿Desactivar a "${confirmDeactivate?.legal_name}"? No podrá usarse en nuevas órdenes.`}
        onConfirm={() => {
          if (confirmDeactivate) {
            deactivate.mutate(confirmDeactivate.id, {
              onSuccess: () => setConfirmDeactivate(null),
            })
          }
        }}
        onCancel={() => setConfirmDeactivate(null)}
        isPending={deactivate.isPending}
        variant="danger"
      />

      <ImportCSVMapperModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        title="Importar Proveedores"
        description="Suba un archivo CSV con los datos de sus proveedores. Si un proveedor ya existe (mismo RUT/ID Tributario), se actualizará."
        systemFields={SUPPLIER_FIELDS}
        templateFileName="plantilla_proveedores.csv"
        onImport={(file, mapping) => importMutation.mutateAsync({ file, mapping })}
        isPending={importMutation.isPending}
      />
    </div>
  )
}
